import { unwrapNormalizedApplicationTransport } from './normalized-application-transport.js';
import { validTimelineReplacement, NORMALIZED_TIMELINE_MAX_BYTES } from './session-observation.js';
import { sameSessionCapabilities } from './normalized-session-capabilities.js';
import { NORMALIZED_EVENT_MAX_BYTES, NORMALIZED_FRAGMENT_BYTES, NORMALIZED_FRAGMENT_TTL_MS, NORMALIZED_FRAME_MAX_BYTES, } from './normalized-transport-types.js';
import { decodeBase64, isRecord, normalizedRoute, sha256Hex, utf8Bytes, validEventId, validNormalizedRoute } from './normalized-transport-codec.js';
const failureFor = (frame, reason) => ({
    ...normalizedRoute(frame), reason, ...(frame.shape === 'normalized_fragment' ? { event_id: frame.event_id } : {}),
});
/** Own one instance per live socket. Semantic event validation follows completion. */
export class NormalizedFragmentAssembler {
    partials = new Map();
    now;
    maxAssemblies;
    maxBytes;
    ttlMs;
    constructor(options = {}) {
        this.now = options.now ?? Date.now;
        this.maxAssemblies = Math.min(4, Math.max(1, options.maxAssemblies ?? 4));
        this.maxBytes = Math.min(4 * NORMALIZED_TIMELINE_MAX_BYTES, Math.max(0, options.maxBytes ?? 4 * NORMALIZED_TIMELINE_MAX_BYTES));
        this.ttlMs = Math.min(NORMALIZED_FRAGMENT_TTL_MS, Math.max(0, options.ttlMs ?? NORMALIZED_FRAGMENT_TTL_MS));
    }
    get bufferedBytes() { return [...this.partials.values()].reduce((sum, partial) => sum + partial.bytes.length + partial.deferredBytes, 0); }
    receive(input, encodedBytes) {
        const failures = this.expire();
        input = unwrapNormalizedApplicationTransport(input);
        if (input === undefined)
            return { status: 'rejected', failures: [...failures, { reason: 'invalid_route' }] };
        if (!isRecord(input) || input.type !== 'agent_activity')
            return { status: 'ignored', failures };
        for (const [key, partial] of this.partials) {
            if (partial.first.logical_shape !== 'normalized_timeline_replacement' && partial.first.agent_id === input.agent_id && partial.first.channel_id !== input.channel_id && typeof input.sequence === 'number' && input.sequence > partial.first.sequence) {
                this.partials.delete(key);
                failures.push(failureFor(partial.first, 'interrupted'));
            }
        }
        if (input.shape === 'normalized' && isRecord(input.event) && input.event.type === 'timeline' && validNormalizedRoute(input) && typeof input.timeline_epoch === 'string') {
            const pending = [...this.partials.values()].find(partial => partial.first.logical_shape === 'normalized_timeline_replacement' && partial.first.timeline_epoch === input.timeline_epoch && partial.first.channel_id === input.channel_id && partial.first.agent_id === input.agent_id && partial.first.session_control_binding_id === input.session_control_binding_id);
            if (pending) {
                if (encodedBytes > NORMALIZED_FRAME_MAX_BYTES || pending.deferred.length >= 4096 || this.bufferedBytes + encodedBytes > this.maxBytes || !isRecord(input.event)) {
                    for (const [key, value] of this.partials)
                        if (value === pending)
                            this.partials.delete(key);
                    return { status: 'rejected', failures: [...failures, failureFor(pending.first, 'assembly_capacity')] };
                }
                pending.deferred.push(structuredClone(input));
                pending.deferredBytes += encodedBytes;
                return { status: 'pending', failures };
            }
        }
        const isReplacement = input.shape === 'normalized_timeline_replacement' || input.logical_shape === 'normalized_timeline_replacement';
        const key = JSON.stringify([input.agent_id, input.channel_id, input.session_control_binding_id ?? null, isReplacement ? input.event_id ?? 'replacement' : 'ordinary']);
        let partial = this.partials.get(key);
        const reject = (reason) => {
            if (partial) {
                this.partials.delete(key);
                failures.push(failureFor(partial.first, reason));
            }
            else {
                failures.push({ ...(validNormalizedRoute(input) ? normalizedRoute(input) : {}), reason,
                    ...(validEventId(input.event_id) ? { event_id: input.event_id } : {}) });
            }
            return { status: 'rejected', failures };
        };
        if (partial && (input.shape !== 'normalized_fragment' || input.sequence !== partial.first.sequence || input.event_id !== partial.first.event_id)) {
            this.partials.delete(key);
            failures.push(failureFor(partial.first, 'interrupted'));
            partial = undefined;
        }
        if (input.shape !== 'normalized' && input.shape !== 'normalized_fragment' && input.shape !== 'normalized_timeline_replacement')
            return { status: 'ignored', failures };
        if (!validNormalizedRoute(input))
            return reject('invalid_route');
        if (!Number.isInteger(encodedBytes) || encodedBytes < 1 || encodedBytes > NORMALIZED_FRAME_MAX_BYTES)
            return reject('frame_limit');
        const replacement = input.shape === 'normalized_timeline_replacement' || input.logical_shape === 'normalized_timeline_replacement';
        const limit = replacement ? NORMALIZED_TIMELINE_MAX_BYTES : NORMALIZED_EVENT_MAX_BYTES;
        if (input.shape === 'normalized' || input.shape === 'normalized_timeline_replacement') {
            if (input.session_control_default && (replacement || !input.session_control_binding_id || !isRecord(input.event) || !['runtime_updated', 'agent_snapshot'].includes(input.event.type)))
                return reject('invalid_event');
            if (!isRecord(input.event) || (input.session_capabilities !== undefined && input.event.type !== 'runtime_updated'))
                return reject('invalid_event');
            try {
                const eventJson = JSON.stringify(input.event);
                if (!isRecord(JSON.parse(eventJson)))
                    return reject('invalid_event');
                if (utf8Bytes(eventJson).length > limit)
                    return reject('event_limit');
                if (replacement && !validTimelineReplacement(input.event, input.protocol_version))
                    return reject('invalid_event');
                const frame = { type: 'agent_activity', shape: input.shape, ...normalizedRoute(input), event: JSON.parse(eventJson) };
                return { status: 'complete', frame, eventJson, failures };
            }
            catch {
                return reject('invalid_event');
            }
        }
        if (input.fragment_version !== 1 || !validEventId(input.event_id)
            || !Number.isInteger(input.total_bytes) || input.total_bytes < 1 || input.total_bytes > limit
            || !Number.isInteger(input.part_index) || !Number.isInteger(input.part_count)
            || input.part_count !== Math.ceil(input.total_bytes / NORMALIZED_FRAGMENT_BYTES)
            || input.part_index < 0 || input.part_index >= input.part_count
            || typeof input.sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(input.sha256)
            || typeof input.data_base64 !== 'string' || input.data_base64.length > Math.ceil(NORMALIZED_FRAGMENT_BYTES / 3) * 4)
            return reject('invalid_fragment');
        const frame = input;
        const bytes = decodeBase64(frame.data_base64);
        const expectedLength = Math.min(NORMALIZED_FRAGMENT_BYTES, frame.total_bytes - frame.part_index * NORMALIZED_FRAGMENT_BYTES);
        if (!bytes || bytes.length !== expectedLength)
            return reject('invalid_fragment');
        if (!partial) {
            if (frame.part_index !== 0)
                return reject('missing_first_part');
            if (this.partials.size >= this.maxAssemblies || this.bufferedBytes + frame.total_bytes > this.maxBytes)
                return reject('assembly_capacity');
            partial = { first: { ...frame, ...normalizedRoute(frame) }, bytes: new Uint8Array(frame.total_bytes), nextIndex: 0, received: 0, expiresAt: this.now() + this.ttlMs, deferred: [], deferredBytes: 0 };
            this.partials.set(key, partial);
        }
        const first = partial.first;
        if (frame.part_index !== partial.nextIndex || frame.channel_id !== first.channel_id || frame.task_id !== first.task_id
            || frame.protocol_version !== first.protocol_version || frame.sequence !== first.sequence || frame.event_id !== first.event_id
            || frame.agent_id !== first.agent_id || frame.occurred_at !== first.occurred_at || frame.part_count !== first.part_count
            || frame.session_control_binding_id !== first.session_control_binding_id
            || (frame.session_control_default === true) !== (first.session_control_default === true)
            || frame.timeline_epoch !== first.timeline_epoch || frame.logical_shape !== first.logical_shape
            || !sameSessionCapabilities(frame.session_capabilities, first.session_capabilities)
            || frame.total_bytes !== first.total_bytes || frame.sha256 !== first.sha256)
            return reject('fragment_conflict');
        partial.bytes.set(bytes, partial.received);
        partial.received += bytes.length;
        partial.nextIndex++;
        if (partial.nextIndex < first.part_count)
            return { status: 'pending', failures };
        this.partials.delete(key);
        if (sha256Hex(partial.bytes) !== first.sha256)
            return reject('digest_mismatch');
        let eventJson;
        try {
            eventJson = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(partial.bytes);
        }
        catch {
            return reject('invalid_utf8');
        }
        let event;
        try {
            event = JSON.parse(eventJson);
        }
        catch {
            return reject('invalid_json');
        }
        if (first.session_control_default && (replacement || !first.session_control_binding_id || !isRecord(event) || !['runtime_updated', 'agent_snapshot'].includes(event.type)))
            return reject('invalid_event');
        if (replacement && !validTimelineReplacement(event, first.protocol_version))
            return reject('invalid_event');
        if (!isRecord(event) || (first.session_capabilities !== undefined && event.type !== 'runtime_updated'))
            return reject('invalid_event');
        return { status: 'complete', following: partial.deferred, frame: { type: 'agent_activity', shape: replacement ? 'normalized_timeline_replacement' : 'normalized', ...normalizedRoute(first), event }, eventJson, failures };
    }
    expire(now = this.now()) {
        const failures = [];
        for (const [key, partial] of this.partials) {
            if (now >= partial.expiresAt) {
                this.partials.delete(key);
                failures.push(failureFor(partial.first, 'expired'));
            }
        }
        return failures;
    }
    clearScope(channelId) {
        for (const [key, partial] of this.partials)
            if (partial.first.channel_id === channelId)
                this.partials.delete(key);
    }
    reset() { this.partials.clear(); }
}
//# sourceMappingURL=normalized-fragment-assembler.js.map